import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Play, 
  Clock, 
  Calendar, 
  User, 
  Award, 
  ExternalLink, 
  Star,
  Users,
  Building2,
  Quote,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { FilmSEO } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform logo components (simple text-based for now, can be replaced with actual SVG logos)
const PlatformIcon = ({ platform }) => {
  const logos = {
    'Amazon Prime': (
      <svg viewBox="0 0 100 30" className="h-6 w-auto">
        <text x="0" y="22" fill="currentColor" fontSize="14" fontWeight="bold">prime video</text>
      </svg>
    ),
    'Apple TV': (
      <svg viewBox="0 0 80 30" className="h-6 w-auto">
        <text x="0" y="22" fill="currentColor" fontSize="14" fontWeight="bold">tv</text>
      </svg>
    ),
  };
  
  return (
    <span className="font-bold text-white">{platform}</span>
  );
};

export default function OlderGodsPage() {
  const [film, setFilm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);

  useEffect(() => {
    const fetchFilm = async () => {
      try {
        const response = await axios.get(`${API}/films/older-gods`);
        setFilm(response.data);
      } catch (error) {
        console.error('Error fetching film:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFilm();
  }, []);

  const nextGalleryImage = () => {
    if (film?.gallery) {
      setActiveGalleryIndex((prev) => (prev + 1) % film.gallery.length);
    }
  };

  const prevGalleryImage = () => {
    if (film?.gallery) {
      setActiveGalleryIndex((prev) => (prev - 1 + film.gallery.length) % film.gallery.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-neutral-950 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!film) {
    return (
      <div className="min-h-screen pt-20 bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-4xl text-white mb-4">FILM NOT FOUND</h1>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="older-gods-page" className="min-h-screen bg-neutral-950">
      {film && <FilmSEO film={film} />}
      {/* Hero Section with Header Image */}
      <section className="relative h-[80vh] flex items-end overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={film.header_url || film.poster_url}
            alt={`${film.title} - Lovecraftian cosmic horror film distributed by LBR Distribution UK`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-neutral-950/80 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 w-full">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            {/* Genre Badge */}
            <span className="inline-block bg-orange-600 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider mb-4">
              {film.genre}
            </span>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl tracking-tight uppercase leading-none text-white mb-4">
              {film.title}
            </h1>

            {/* Tagline */}
            <p className="text-orange-500 text-xl md:text-2xl font-medium italic mb-6">
              A Lovecraftian descent into madness
            </p>

            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-6 text-neutral-300 mb-8">
              <div className="flex items-center gap-2">
                <Calendar size={18} className="text-orange-500" />
                <span>{film.year}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} className="text-orange-500" />
                <span>{film.runtime}</span>
              </div>
              <div className="flex items-center gap-2">
                <User size={18} className="text-orange-500" />
                <span>Dir. {film.director}</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 size={18} className="text-orange-500" />
                <span>{film.production_company}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4">
              <a
                href={film.trailer_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="watch-trailer-btn"
                className="flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
              >
                <Play size={20} fill="white" />
                WATCH TRAILER
              </a>
              <a
                href={film.platform_links?.amazon_us || '#'}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="watch-now-btn"
                className="flex items-center gap-3 bg-white hover:bg-neutral-200 text-black font-sans font-bold py-4 px-8 uppercase tracking-widest text-sm transition-all"
              >
                WATCH NOW
                <ExternalLink size={18} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Synopsis Section */}
      <section className="py-16 md:py-24 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Poster */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={film.poster_url}
                alt={`${film.title} official movie poster - watch on Amazon Prime, Apple TV`}
                className="w-full max-w-sm mx-auto aspect-[2/3] object-cover shadow-2xl"
              />
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2"
            >
              <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-6">
                SYNOPSIS
              </h2>
              <p className="text-neutral-300 text-lg md:text-xl leading-relaxed mb-8">
                {film.synopsis}
              </p>

              {/* Awards */}
              {film.awards && film.awards.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-display text-xl tracking-wider text-white uppercase mb-4 flex items-center gap-2">
                    <Award className="text-orange-500" size={24} />
                    AWARDS & RECOGNITION
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {film.awards.map((award, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-orange-600/10 border border-orange-500/30 px-4 py-2"
                      >
                        <Star size={16} className="text-orange-500" />
                        <span className="text-neutral-200 text-sm">{award}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cast */}
              {film.cast && (
                <div className="mb-8">
                  <h3 className="font-display text-xl tracking-wider text-white uppercase mb-4 flex items-center gap-2">
                    <Users className="text-orange-500" size={24} />
                    CAST
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {film.cast.map((actor, index) => (
                      <span
                        key={index}
                        className="bg-neutral-900 border border-white/10 px-4 py-2 text-neutral-300 text-sm"
                      >
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Crew */}
              {film.crew && (
                <div>
                  <h3 className="font-display text-xl tracking-wider text-white uppercase mb-4">
                    CREW
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {film.crew.map((member, index) => (
                      <div key={index} className="bg-neutral-900 border border-white/10 p-4">
                        <span className="text-orange-500 text-xs uppercase tracking-wider block mb-1">
                          {member.role}
                        </span>
                        <span className="text-white font-medium">{member.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Where to Watch */}
      <section className="py-16 md:py-24 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-4">
              WHERE TO WATCH
            </h2>
            <p className="text-neutral-400 text-lg">
              Available now on major streaming platforms worldwide
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {film.platforms.map((platform, index) => {
              let link = '#';
              if (platform === 'Amazon Prime' && film.platform_links?.amazon_us) {
                link = film.platform_links.amazon_us;
              } else if (platform === 'Apple TV' && film.platform_links?.apple_tv) {
                link = film.platform_links.apple_tv;
              }
              
              return (
                <motion.a
                  key={platform}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`platform-${platform.toLowerCase().replace(' ', '-')}`}
                  className="flex items-center justify-center gap-2 bg-neutral-900 border border-white/10 hover:border-orange-500 p-6 transition-all group"
                >
                  <span className="font-display text-lg text-white group-hover:text-orange-500 transition-colors uppercase tracking-wider">
                    {platform}
                  </span>
                  <ExternalLink size={16} className="text-neutral-500 group-hover:text-orange-500 transition-colors" />
                </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery */}
      {film.gallery && film.gallery.length > 0 && (
        <section className="py-16 md:py-24 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-4">
                GALLERY
              </h2>
            </motion.div>

            {/* Main Gallery Image */}
            <div className="relative mb-6">
              <motion.img
                key={activeGalleryIndex}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                src={film.gallery[activeGalleryIndex]}
                alt={`${film.title} movie scene ${activeGalleryIndex + 1} - Welsh Lovecraftian horror`}
                className="w-full aspect-video object-cover"
              />
              
              {/* Navigation Arrows */}
              <button
                onClick={prevGalleryImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-orange-600 transition-colors"
              >
                <ChevronLeft size={24} className="text-white" />
              </button>
              <button
                onClick={nextGalleryImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-orange-600 transition-colors"
              >
                <ChevronRight size={24} className="text-white" />
              </button>
            </div>

            {/* Thumbnails */}
            <div className="flex justify-center gap-4">
              {film.gallery.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setActiveGalleryIndex(index)}
                  className={`w-24 h-16 overflow-hidden border-2 transition-colors ${
                    index === activeGalleryIndex ? 'border-orange-500' : 'border-transparent'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      {film.reviews && film.reviews.length > 0 && (
        <section className="py-16 md:py-24 bg-neutral-900/50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-4">
                PRESS & REVIEWS
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {film.reviews.map((review, index) => (
                <motion.a
                  key={index}
                  href={review.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="p-6 bg-neutral-900 border border-white/10 hover:border-orange-500 transition-colors group"
                >
                  {review.quote && (
                    <div className="mb-4">
                      <Quote size={24} className="text-orange-500 mb-2" />
                      <p className="text-neutral-300 italic">"{review.quote}"</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{review.source}</span>
                    <ExternalLink size={16} className="text-neutral-500 group-hover:text-orange-500 transition-colors" />
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Official Website CTA */}
      {film.official_website && (
        <section className="py-16 md:py-24 bg-neutral-950">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-3xl md:text-4xl tracking-wider text-white uppercase mb-6">
                LEARN MORE
              </h2>
              <p className="text-neutral-400 text-lg mb-8">
                Visit the official Older Gods website for behind-the-scenes content, the making-of podcast, and more.
              </p>
              <a
                href={film.official_website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="official-website-btn"
                className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-500 text-white font-sans font-bold py-4 px-10 uppercase tracking-widest text-sm transition-all duration-300 hover:tracking-[0.2em]"
              >
                VISIT OFFICIAL WEBSITE
                <ExternalLink size={18} />
              </a>
            </motion.div>
          </div>
        </section>
      )}

      {/* Related Links Section */}
      <section className="py-16 bg-neutral-900/30 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-display text-lg tracking-wider text-white uppercase mb-4">EXPLORE MORE FILMS</h3>
              <Link to="/films" className="text-neutral-400 hover:text-orange-500 transition-colors block mb-2">
                View Our Full Catalogue →
              </Link>
              <Link to="/films/patriarch" className="text-neutral-400 hover:text-orange-500 transition-colors block">
                Patriarch (Coming 2026) →
              </Link>
            </div>
            <div>
              <h3 className="font-display text-lg tracking-wider text-white uppercase mb-4">OUR SERVICES</h3>
              <Link to="/distribution" className="text-neutral-400 hover:text-orange-500 transition-colors block mb-2">
                VOD Distribution Services →
              </Link>
              <Link to="/sales" className="text-neutral-400 hover:text-orange-500 transition-colors block mb-2">
                Film Sales Representation →
              </Link>
              <Link to="/marketing" className="text-neutral-400 hover:text-orange-500 transition-colors block">
                Marketing Agency →
              </Link>
            </div>
            <div>
              <h3 className="font-display text-lg tracking-wider text-white uppercase mb-4">FILMMAKERS</h3>
              <Link to="/submit" className="text-neutral-400 hover:text-orange-500 transition-colors block mb-2">
                Submit Your Film →
              </Link>
              <Link to="/faq" className="text-neutral-400 hover:text-orange-500 transition-colors block mb-2">
                FAQ →
              </Link>
              <Link to="/contact" className="text-neutral-400 hover:text-orange-500 transition-colors block">
                Contact Us →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
